import React, { useState, useEffect } from 'react';
import { Form, Button, Alert, ButtonGroup, Row, Col } from 'react-bootstrap';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faCheckCircle, faSpinner } from '@fortawesome/free-solid-svg-icons';

type Option = string;

interface CustomSurveyProps {
    title: string;
    altTitle: string;
    description?: string;
    options: Option[];
    onSubmit: (data: { option?: Option; options?: Option[]; comment?: string }) => void;
    isSubmitted?: boolean;
    isUpdate?: boolean;
    submittedAt?: string;
    enableComment?: boolean;

    // New props
    initialSelectedOption?: Option; // For single select
    initialSelectedOptions?: Option[]; // For multi select
    initialComment?: string;

    isMultiSelect?: boolean; // Toggle mode
    useSegmentedButtons?: boolean;

    optionToValueMap?: any;
}

const CustomSurveyForm: React.FC<CustomSurveyProps> = ({
    title,
    altTitle,
    description,
    options,
    onSubmit,
    isSubmitted = false,
    isUpdate = false,
    submittedAt,
    enableComment = false,
    initialSelectedOption = undefined,
    initialSelectedOptions = [],
    initialComment = '',
    isMultiSelect = false, // Default is single select
    useSegmentedButtons = false,
    optionToValueMap,
}) => {
    const normalizedInitialOptions: Option[] = isMultiSelect
        ? (initialSelectedOptions.length > 0
            ? [...initialSelectedOptions]
            : (initialSelectedOption ? [initialSelectedOption] : []))
        : initialSelectedOptions;

    const [selectedOption, setSelectedOption] = useState<Option | undefined>(initialSelectedOption);
    const [selectedOptions, setSelectedOptions] = useState<Option[]>(normalizedInitialOptions);
    const [comment, setComment] = useState<string>(initialComment);
    const [isSaving, setIsSaving] = useState(false);
    const [hasSubmitted, setHasSubmitted] = useState(
        isMultiSelect ? normalizedInitialOptions.length > 0 : !!initialSelectedOption
    );
    const [lastSubmittedOption, setLastSubmittedOption] = useState<Option | undefined>(initialSelectedOption);
    const [lastSubmittedOptions, setLastSubmittedOptions] = useState<Option[]>(normalizedInitialOptions);
    const [lastSubmittedComment, setLastSubmittedComment] = useState<string>(initialComment);

    const autoSubmit = (optionOrOptions: Option | Option[], currentComment: string) => {
        if (isSaving) return; // Prevent double submission

        setIsSaving(true);
        setHasSubmitted(true);

        if (isMultiSelect) {
            setLastSubmittedOptions(optionOrOptions as Option[]);
            setLastSubmittedComment(currentComment);
            onSubmit({ options: optionOrOptions as Option[], comment: enableComment ? currentComment : undefined });
        } else {
            setLastSubmittedOption(optionOrOptions as Option);
            setLastSubmittedComment(currentComment);
            onSubmit({ option: optionOrOptions as Option, comment: enableComment ? currentComment : undefined });
        }

        setTimeout(() => {
            setIsSaving(false);
        }, 1000);
    };

    const handleOptionChange = (option: Option) => {
        if (isMultiSelect) {
            let updatedOptions: Option[];
            if (selectedOptions.includes(option)) {
                updatedOptions = selectedOptions.filter((o) => o !== option);
            } else {
                updatedOptions = [...selectedOptions, option];
            }
            setSelectedOptions(updatedOptions);

            // Auto-submit on multi-select change
            if (updatedOptions.length > 0) {
                autoSubmit(updatedOptions, comment);
            }
        } else {
            setSelectedOption(option);
            // Auto-submit on single select change
            autoSubmit(option, comment);
        }
    };

    function useDebounce<T>(value: T, delay: number): T {
        const [debouncedValue, setDebouncedValue] = useState(value);

        useEffect(() => {
            const handler = setTimeout(() => {
                setDebouncedValue(value);
            }, delay);

            return () => {
                clearTimeout(handler);
            };
        }, [value, delay]);

        return debouncedValue;
    }

    const debouncedComment = useDebounce(comment, 500);
    useEffect(() => {
        // Only auto-submit if there's something selected
        if (isMultiSelect && selectedOptions.length > 0) {
            autoSubmit(selectedOptions, debouncedComment);
        } else if (!isMultiSelect && selectedOption) {
            autoSubmit(selectedOption, debouncedComment);
        }
    }, [debouncedComment]);

    // const handleSubmit = () => {
    //     if (isMultiSelect && selectedOptions.length === 0) return;
    //     if (!isMultiSelect && !selectedOption) return;

    //     setIsSaving(true);
    //     setHasSubmitted(true);

    //     if (isMultiSelect) {
    //         setLastSubmittedOptions(selectedOptions);
    //         setLastSubmittedComment(comment);
    //         onSubmit({ options: selectedOptions, comment: enableComment ? comment : undefined });
    //     } else {
    //         setLastSubmittedOption(selectedOption);
    //         setLastSubmittedComment(comment);
    //         onSubmit({ option: selectedOption, comment: enableComment ? comment : undefined });
    //     }

    //     setTimeout(() => {
    //         setIsSaving(false);
    //     }, 1000);
    // };

    const arraysAreEqual = (a: Option[], b: Option[]) => {
        if (a.length !== b.length) return false;
        const sortedA = [...a].sort();
        const sortedB = [...b].sort();
        return sortedA.every((val, index) => val === sortedB[index]);
    };

    const hasChanges = isMultiSelect
        ? !arraysAreEqual(selectedOptions, lastSubmittedOptions) || (enableComment && comment !== lastSubmittedComment)
        : selectedOption !== lastSubmittedOption || (enableComment && comment !== lastSubmittedComment);

    return (
        <Form className="survey-form">
            {isSubmitted && submittedAt && (
                <Alert variant="success" className="mb-3">
                    <FontAwesomeIcon icon={faCheckCircle} />{' '}
                    {isUpdate ? 'Updated' : 'Submitted'} on {new Date(submittedAt).toLocaleString()}
                </Alert>
            )}

            <div><strong>{title}</strong></div>
            {description && <p>{description}</p>}

            {useSegmentedButtons ? (
                <ButtonGroup className="w-100 mb-3">
                    {options.map((option, index) => (
                        <Button
                            key={index}
                            variant={selectedOption === option ? "secondary" : "outline-secondary"}
                            onClick={() => handleOptionChange(option)}
                            className="flex-fill"
                            size='sm'
                        >
                            {optionToValueMap && optionToValueMap[option] ? optionToValueMap[option] : option}
                        </Button>
                    ))}
                </ButtonGroup>
            ) : (
                <Form.Group className="mb-3">
                    {options.map((option, index) => (
                        <Form.Check
                            key={index}
                            type={isMultiSelect ? 'checkbox' : 'radio'}
                            id={`option-${altTitle.replaceAll(' ', '_')}-${index}`}
                            name="custom-survey-option"
                            label={option}
                            value={option}
                            checked={isMultiSelect ? selectedOptions.includes(option) : selectedOption === option}
                            onChange={() => handleOptionChange(option)}
                            className="mb-2"
                        />
                    ))}
                </Form.Group>
            )}

            {enableComment && (
                <Form.Group className="mb-3">
                    <Form.Label className="text-muted small"><strong>Additional Comments (Optional)</strong></Form.Label>
                    <Form.Control
                        as="textarea"
                        rows={3}
                        value={comment}
                        onChange={(e) => setComment(e.target.value)}
                    />
                </Form.Group>
            )}

            <div className="mt-1 text-right text-small">
                {isSaving ? (
                    <div>
                        <FontAwesomeIcon icon={faSpinner} spin /> Saving...
                    </div>
                ) : !hasSubmitted || hasChanges ? (
                    <div>
                        Not Saved
                    </div>
                ) : (
                    <div className="text-success">
                        <FontAwesomeIcon icon={faCheckCircle} /> Saved
                    </div>
                )}
            </div>

        </Form>
    );
};

export default CustomSurveyForm;


export const getCombinedSectionToRate = (
    handleSave: any,
    feedbackTargetToSurvey: any,
    title: string,
    data: string | string[],
    reasoningData: string,
    surveyIsCategorical: boolean,
    surveyDescription: string,
    surveyOptions: string[],
    surveyKey: string,
    reasoningSurveyTitle: string,
    reasoningSurveyDescription: string,
    reasoningSurveyOptions: string[],
    reasoningSurveyKey: string,
    isMultiSelect?: boolean,
    optionToValueMap?: any,
) => {
    return (
        <>
            {/* Question Header spanning both columns with box */}
            <Row className="mt-3">
                <Col>
                    <div className="survey-question-header mb-2">
                        <div>
                            {title}
                        </div>
                        {surveyDescription && (
                            <div className="small mt-1" style={{fontWeight: 'normal'}}>
                                {surveyDescription}
                            </div>
                        )}
                    </div>
                </Col>
            </Row>
            {/* Column Headers */}
            <Row className="mb-2">
                <Col>
                    <div className="text-muted small">
                        <strong>Capacity Assessment</strong>
                    </div>
                </Col>
                <Col>
                    <div className="text-muted small">
                        <strong>MD Assessment</strong>
                    </div>
                </Col>
            </Row>
            {/* Content Row */}
            <Row>
                <Col>
                    {Array.isArray(data) ? (
                        <div className="survey-context mb-2">
                            <ul>
                                {data.map((item, index) => (
                                    <li key={index}>
                                        {(optionToValueMap && optionToValueMap[item]) ? optionToValueMap[item] : item}
                                    </li>
                                ))}
                            </ul>
                        </div>
                    ) : (
                        <div className="survey-context mb-2">
                            {(optionToValueMap && optionToValueMap[data]) ? optionToValueMap[data] : data}
                        </div>
                    )}
                    <div className="text-muted small mb-2">
                        <strong>Capacity Reasoning</strong>
                    </div>
                    <div className="survey-context">
                        {reasoningData}
                    </div>
                </Col>
                <Col>
                    {/* Primary Assessment */}
                    <div className="mb-4">
                        <CustomSurveyForm
                            title={""}
                            altTitle={title}
                            description={undefined}
                            options={surveyOptions}
                            onSubmit={(data) => handleSave(surveyKey, data)}
                            enableComment={false}
                            initialSelectedOption={feedbackTargetToSurvey[surveyKey] ? feedbackTargetToSurvey[surveyKey].option : undefined}
                            initialSelectedOptions={feedbackTargetToSurvey[surveyKey] ? feedbackTargetToSurvey[surveyKey].options : undefined}
                            initialComment={feedbackTargetToSurvey[surveyKey] ? feedbackTargetToSurvey[surveyKey].comment : undefined}
                            useSegmentedButtons={!surveyIsCategorical}
                            isMultiSelect={isMultiSelect}
                            optionToValueMap={optionToValueMap}
                        />
                    </div>

                    {/* Reasoning Quality Assessment */}
                    <div>
                        <div className="text-muted small mb-2">
                            <strong>MD Rating of Capacity Reasoning</strong>
                        </div>
                        <CustomSurveyForm
                            title={""}
                            altTitle="MD Rating of Capacity Reasoning"
                            description={undefined}
                            options={reasoningSurveyOptions}
                            onSubmit={(data) => handleSave(reasoningSurveyKey, data)}
                            enableComment={true}
                            initialSelectedOption={feedbackTargetToSurvey[reasoningSurveyKey] ? feedbackTargetToSurvey[reasoningSurveyKey].option : undefined}
                            initialComment={feedbackTargetToSurvey[reasoningSurveyKey] ? feedbackTargetToSurvey[reasoningSurveyKey].comment : undefined}
                            useSegmentedButtons={true}
                            optionToValueMap={optionToValueMap}
                        />
                    </div>
                </Col>
            </Row>
        </>
    );
};
